import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Typography,
  useTheme,
  Autocomplete,
  Box,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import axios from "axios";
import config from "../../config";
import { NumericFormat } from "react-number-format";

const promotionTypes = [
  "basic",
  "flash",
  "vip",
  "combo",
  "quantity",
  "birthday",
  "category",
];

const UpdatePromotionModal = ({ open, onClose, promotion, onSave }) => {
  const theme = useTheme();
  const labelColor = theme.palette.mode === "dark" ? "#fff" : "#000";
  const labelProps = { style: { color: labelColor } };

  const [formData, setFormData] = useState({});
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && promotion) {
      fetchProducts();
      fetchCategories();
      setFormData({
        productId: promotion.productId || "",
        categoryId: promotion.categoryId || "",
        discountValue: promotion.discountValue || "",
        discountType: promotion.discountType || "",
        type: promotion.type || "basic",
        condition: promotion.conditionJson
          ? JSON.parse(promotion.conditionJson)
          : {},
        startDate: dayjs(promotion.startDate),
        endDate: dayjs(promotion.endDate),
      });
    }
  }, [open, promotion]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${config.apiBaseUrl}/Products`);
      setProducts(res.data || []);
    } catch (err) {
      console.error("Lỗi tải sản phẩm:", err);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${config.apiBaseUrl}/Categories`);
      setCategories(res.data || []);
    } catch (err) {
      console.error("Lỗi tải danh mục:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleConditionChange = (key, value, index = null) => {
    setFormData((prev) => {
      const updated = { ...prev.condition };
      if (prev.type === "quantity" && index !== null) {
        updated.tiers[index][key] = value;
      } else {
        updated[key] = value;
      }
      return { ...prev, condition: updated };
    });
  };

  const renderConditionFields = () => {
    const { type, condition } = formData;
    switch (type) {
      case "flash":
        return (
          <>
            <TextField
              label="Thời gian bắt đầu"
              value={condition.flashStart || ""}
              onChange={(e) =>
                handleConditionChange("flashStart", e.target.value)
              }
              fullWidth
              InputLabelProps={labelProps}
            />
            <TextField
              label="Thời gian kết thúc"
              value={condition.flashEnd || ""}
              onChange={(e) =>
                handleConditionChange("flashEnd", e.target.value)
              }
              fullWidth
              sx={{ mt: 1 }}
              InputLabelProps={labelProps}
            />
          </>
        );
      case "vip":
        return (
          <TextField
            label="Cấp độ VIP"
            value={condition.minLevel || ""}
            onChange={(e) => handleConditionChange("minLevel", e.target.value)}
            fullWidth
            InputLabelProps={labelProps}
          />
        );
      case "quantity":
        return condition.tiers?.map((tier, index) => (
          <Box key={index} sx={{ display: "flex", gap: 2, mt: 1 }}>
            <TextField
              label={`Tối thiểu mua #${index + 1}`}
              value={tier.min}
              onChange={(e) =>
                handleConditionChange("min", e.target.value, index)
              }
              type="number"
              fullWidth
              InputLabelProps={labelProps}
            />
            <TextField
              label="Giảm (%)"
              value={tier.discount}
              onChange={(e) =>
                handleConditionChange("discount", e.target.value, index)
              }
              type="number"
              fullWidth
              InputLabelProps={labelProps}
            />
          </Box>
        ));
      default:
        return null;
    }
  };

  const handleSubmit = async () => {
    setError("");
    try {
      const payload = {
        discountId: promotion.discountId || null,
        productId: parseInt(formData.productId) || null,
        categoryId: parseInt(formData.categoryId) || null,
        discountValue: parseFloat(formData.discountValue),
        discountType: formData.discountType,
        type: formData.type,
        conditionJson: JSON.stringify(formData.condition || {}),
        startDate: formData.startDate.format("YYYY-MM-DD"),
        endDate: formData.endDate.format("YYYY-MM-DD"),
      };

      await axios.put(
        `${config.apiBaseUrl}/Discounts/${promotion.discountId}`,
        payload
      );
      alert("✅ Cập nhật thành công!");
      if (onSave) onSave(payload);
      onClose();
    } catch (err) {
      const msg = err?.response?.data;
      setError(typeof msg === "string" ? `❌ ${msg}` : "❌ Đã xảy ra lỗi.");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Cập nhật khuyến mãi</DialogTitle>
      <DialogContent dividers>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Autocomplete
                options={products}
                getOptionLabel={(option) => option.name || ""}
                value={
                  products.find((p) => p.productId === +formData.productId) ||
                  null
                }
                onChange={(_, value) =>
                  setFormData((prev) => ({
                    ...prev,
                    productId: value?.productId || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Sản phẩm"
                    fullWidth
                    InputLabelProps={labelProps}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Autocomplete
                options={categories}
                getOptionLabel={(option) => option.name || ""}
                value={
                  categories.find(
                    (c) => c.categoryId === +formData.categoryId
                  ) || null
                }
                onChange={(_, value) =>
                  setFormData((prev) => ({
                    ...prev,
                    categoryId: value?.categoryId || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Danh mục"
                    fullWidth
                    InputLabelProps={labelProps}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <NumericFormat
                label="Giá trị giảm"
                value={formData.discountValue}
                onValueChange={({ floatValue }) =>
                  setFormData((prev) => ({
                    ...prev,
                    discountValue: floatValue || 0,
                  }))
                }
                customInput={TextField}
                thousandSeparator="."
                decimalSeparator=","
                allowNegative={false}
                fullWidth
                InputLabelProps={labelProps}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Loại giảm"
                name="discountType"
                value={formData.discountType}
                onChange={handleChange}
                fullWidth
                InputLabelProps={labelProps}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Loại khuyến mãi"
                name="type"
                value={formData.type}
                onChange={(e) => {
                  const newType = e.target.value;
                  const defaultCondition = {
                    flash: { flashStart: "", flashEnd: "" },
                    vip: { minLevel: "" },
                    combo: { requiredProductIds: "" },
                    quantity: { tiers: [{ min: "", discount: "" }] },
                    birthday: { daysBefore: "", daysAfter: "" },
                  };
                  setFormData((prev) => ({
                    ...prev,
                    type: newType,
                    condition: defaultCondition[newType] || {},
                  }));
                }}
                fullWidth
                InputLabelProps={labelProps}
              >
                {promotionTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {formData.type !== "basic" && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Điều kiện áp dụng
                </Typography>
                {renderConditionFields()}
              </Grid>
            )}

            <Grid item xs={6}>
              <DatePicker
                label="Ngày bắt đầu"
                value={formData.startDate}
                onChange={(newValue) =>
                  setFormData((prev) => ({ ...prev, startDate: newValue }))
                }
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: labelProps,
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label="Ngày kết thúc"
                value={formData.endDate}
                onChange={(newValue) =>
                  setFormData((prev) => ({ ...prev, endDate: newValue }))
                }
                format="DD/MM/YYYY"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    InputLabelProps: labelProps,
                  },
                }}
              />
            </Grid>
            {formData.productId &&
              (() => {
                const selectedProduct = products.find(
                  (p) => p.productId === +formData.productId
                );
                const basePrice = selectedProduct?.basePrice || 0;
                const discount = parseFloat(formData.discountValue || 0);
                let finalPrice = basePrice;

                if (formData.discountType === "percent") {
                  finalPrice = basePrice - (basePrice * discount) / 100;
                } else if (formData.discountType === "amount") {
                  finalPrice = basePrice - discount;
                }

                return (
                  <Grid item xs={12}>
                    <Box sx={{ mt: 1 }}>
                      <Typography>
                        💰 <strong>Giá gốc:</strong>{" "}
                        {basePrice.toLocaleString()} đ
                      </Typography>
                      <Typography>
                        ✅ <strong>Giá sau giảm:</strong>{" "}
                        <span style={{ color: "green" }}>
                          {Math.max(finalPrice, 0).toLocaleString()} đ
                        </span>
                      </Typography>
                    </Box>
                  </Grid>
                );
              })()}
            {error && (
              <Grid item xs={12}>
                <Typography color="error">{error}</Typography>
              </Grid>
            )}
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Hủy</Button>
        <Button variant="contained" onClick={handleSubmit}>
          Cập nhật
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdatePromotionModal;
